import {
  DependencyIndex,
  type AttdefEntity,
  type DrawingEntity,
} from "@dxf-ai-editor/drawing-model";

import {
  createEntityMetadataIndex,
  type EntityMetadataIndex,
} from "./entityMetadataIndex";
import {
  createCompactStore,
  entityFromCompactStore,
  type CompactStore,
  type CompactStoreKind,
} from "./compactStores/aggregateStore";
import type { BaseColumns } from "./compactStores/compactStoreBase";

/**
 * Phase 4 Wave 1 — per-document non-reactive state.
 *
 * Each open drawing owns one holder. The mutationStore module keeps a
 * `Map<documentId, DocumentMemoryHolder>` plus an "active document"
 * pointer; existing module-level exports route through the active
 * holder via `activeHolder()` so legacy single-document call sites
 * continue to work unchanged.
 *
 * Why everything in one struct (not separate maps per piece of state):
 *  - Atomic switch — `setActiveDocument` flips one pointer, never a
 *    temporary inconsistent (this-doc's-cache + other-doc's-index)
 *    combo.
 *  - `clearDocument` drops the whole bundle; no orphaned half-state.
 *  - Per-doc version counter — `getMetadataIndexVersion()` returns the
 *    active doc's revision; React subscribers tied to one doc are not
 *    spuriously invalidated by mutations in another.
 */

/**
 * Phase 4 Wave 1 — locate (spaceId, entityId) in the compact cache.
 * Returns the per-type store name + index where the entity lives, or
 * `undefined` if not found.
 *
 * Promoted here (from mutationStore) so the holder type and the location
 * record share a single source file — every consumer that imports the
 * location type already knows about the holder.
 */
export interface CompactEntityLocation {
  /**
   * The per-type store this entity lives in. Aliased to `CompactStoreKind`
   * (aggregateStore) rather than re-spelled here: the two lists were
   * hand-duplicated and silently drifted the moment a new per-type store was
   * added, which is a second truth for one fact.
   */
  storeKind: CompactStoreKind;
  index: number;
}

export interface DocumentMemoryHolder {
  compactStore: CompactStore;
  idIndex: Map<string, CompactEntityLocation>;
  metadataIndex: EntityMetadataIndex;
  /**
   * PR2 (plan §13) — reverse dependency index: refName → entities referencing
   * it, per ref-kind (layer / style / block / linetype), valued by
   * `EntityRef{container,id}`. INDEX-OVER-EXISTING (D5): no `DrawingEntity` is
   * allocated; built once on import from the compact-store-materialised /
   * document entities, then incrementally updated by `commitMutation` (single
   * choke point). Supplies the id-SETS the metadata index lacks (which keeps
   * layer COUNTS only) plus the style/block/linetype kinds. Consumed by PURGE
   * (and, in a follow-up, layer/style delete preflight + BCOUNT).
   *
   * Reset by `clearMutationStore` and `clearCompactCache`.
   */
  dependencyIndex: DependencyIndex;
  metadataIndexVersion: number;
  /**
   * Phase 4 Wave 2 (П35Р follow-up) — monotonic counter bumped by every
   * compact-cache write (`appendToCompactCache`,
   * `removeFromCompactCache`, `setInCompactCache`, `populateCompactCache*`,
   * `clearCompactCache`, `clearMutationStore`). Used by
   * `wrapSpaceAsReadThrough` to memoise the per-space snapshot — the
   * O(N) `snapshotSpaceEntities` walk runs once per (cacheVersion,
   * spaceId) pair instead of once per `.entities` getter access.
   *
   * Distinct from `metadataIndexVersion` (which is bumped explicitly by
   * `commitMutation` for reactive selector invalidation, NOT by
   * lower-level cache helpers).
   */
  cacheVersion: number;
  /**
   * Phase 4 Wave 1 codex P2 + Wave 2 (F2 follow-up) — sticky bit set on
   * first `populateCompactCache` / `appendToCompactCache` for this
   * holder. Bulk-populate import paths also set it via
   * `populateCompactCacheFromDocument`. Never cleared by removes /
   * tombstones, so a later "drained" state (idIndex.size === 0) is
   * still distinguishable from a "never populated" holder. Used by
   * `wrapSpaceAsReadThrough` to decide whether to trust the snapshot
   * (`[]` for legitimately-drained space) or fall back to inline
   * `space.entities` (legacy small-drawing path that bypassed the
   * cache).
   *
   * Reset by `clearMutationStore` and `clearCompactCache`.
   */
  cachePopulatedOnce: boolean;
  /**
   * PR1b Codex P2 FIX-3/FIX-4 — the set of space ids whose INLINE
   * `space.entities[]` were DISCARDED by the streaming import path
   * (`discardEntitiesAfterStreaming: true`). For these spaces the compact
   * cache — NOT the inline array — is the authoritative / complete entity
   * source, so `buildExportEntitiesSnapshot` MUST surface the cache snapshot
   * (even an empty `[]` for a drained space) rather than the stale retained
   * inline shell (e.g. a paper space that retains only an inline Viewport).
   *
   * Populated per-chunk by the streaming chunk consumer
   * (`markSpaceStreamed`); a space id present here means "streaming drained
   * this space's inline entities into the cache". A space ABSENT here was
   * imported inline (legacy/bulk path) and its `space.entities[]` is the
   * full-fidelity export source (preserves per-entity `linetypeScale`,
   * DXF group 48 — which the compact base store does NOT retain).
   *
   * Distinct from `cachePopulatedOnce` (a GLOBAL sticky flag): an inline
   * bulk import also flips `cachePopulatedOnce` via
   * `populateCompactCacheFromDocument`, so that flag CANNOT distinguish an
   * inline-imported space from a streaming-discarded one. Inline-emptiness
   * is also insufficient: a partially-retained paper space has a non-empty
   * inline Viewport yet is cache-authoritative, while a clean empty model
   * space is inline-authoritative — only this EXPLICIT per-space marker
   * separates the two.
   *
   * Reset by `clearMutationStore` and `clearCompactCache`.
   */
  streamedSpaceIds: Set<string>;
}

export function createDocumentMemoryHolder(): DocumentMemoryHolder {
  return {
    compactStore: createCompactStore(),
    idIndex: new Map(),
    metadataIndex: createEntityMetadataIndex(),
    dependencyIndex: new DependencyIndex(),
    metadataIndexVersion: 0,
    cacheVersion: 0,
    cachePopulatedOnce: false,
    streamedSpaceIds: new Set(),
  };
}

/**
 * Phase 4 Wave 1 Task B/C — drain a holder's compact cache and return
 * all live (non-tombstoned) entities whose `spaceId` matches.
 *
 * Used by:
 *  - DXF write path — caller takes a snapshot before serialise so the
 *    writer iterates a stable array even if the cache is mutated mid-
 *    export.
 *  - `wrapSpaceAsReadThrough(...).entities` getter — Task B materializes
 *    the per-space entity array on each access instead of duplicating
 *    storage in the reactive `geometryDocument.spaces[].entities`.
 *
 * **Identity contract:** each call returns freshly-constructed entity
 * objects (the underlying `entityFromCompactStore` re-builds them per
 * call). Callers that need stable `===` identity must memoise on the
 * outer array reference or on the entity id.
 *
 * Cost: O(total cache count) per call — walks every per-type column
 * once, filters by `spaceId`. For the hot writer path one snapshot per
 * serialise is acceptable (the writer was already O(N)). Phase 4 Wave
 * 2 will add per-space buckets to cut this to O(live-in-space).
 */
export function snapshotSpaceEntitiesFromHolder(
  holder: DocumentMemoryHolder,
  spaceId: string,
): DrawingEntity[] {
  const store = holder.compactStore;
  const out: DrawingEntity[] = [];

  // Phase 4 Wave 1 codex F2/F3 — walk `idIndex` (a Map; preserves
  // insertion order). This:
  //  - Preserves original cross-type insertion order (z-order parity
  //    with reactive `space.entities` consumers, e.g. spatialBuilder).
  //  - Drops shadowed-duplicate slots: `appendToCompactCache` on an
  //    existing id leaves the old slot live but rewrites `idIndex` —
  //    the Map only contains the current (non-shadowed) location.
  //
  // Attdefs are block-children only — `idIndex` may contain them but
  // they're filtered after materialisation to match the
  // `getEntityFromCompactCache` contract (returns `undefined` for
  // ATTDEF) and the spaceId filter.
  for (const location of holder.idIndex.values()) {
    const { storeKind, index } = location;
    // Skip ATTDEF kind early — never lives in model/paper space.
    if (storeKind === "attdefs") continue;

    // LINE — separate columns (Iter 2 frozen for measurement parity).
    if (storeKind === "lines") {
      if (store.lines.tombstones[index] === 1) continue;
      if (store.lines.spaceIds[index] !== spaceId) continue;
      const entity = entityFromCompactStore(store, "lines", index);
      if (entity && entity.type !== "Attdef") out.push(entity);
      continue;
    }

    const base = baseColumnsForKind(store, storeKind);
    if (base === undefined) continue;
    if (base.tombstones[index] === 1) continue;
    if (base.spaceIds[index] !== spaceId) continue;
    const entity = entityFromCompactStore(store, storeKind, index);
    if (entity && entity.type !== "Attdef") out.push(entity);
  }

  return out;
}

/**
 * Phase 4 Wave 1 codex F2/F3 — resolve a per-kind `BaseColumns` for the
 * non-line stores. Returns `undefined` for `lines` (handled separately)
 * and `attdefs` (filtered out at the call site).
 */
function baseColumnsForKind(
  store: CompactStore,
  kind: CompactStoreKind,
): BaseColumns | undefined {
  switch (kind) {
    case "points":
      return store.points.base;
    case "circles":
      return store.circles.base;
    case "arcs":
      return store.arcs.base;
    case "ellipses":
      return store.ellipses.base;
    case "splines":
      return store.splines.base;
    case "lwpolylines":
      return store.lwpolylines.base;
    case "texts":
      return store.texts.base;
    case "mtexts":
      return store.mtexts.base;
    case "dimensions":
      return store.dimensions.base;
    case "multileaders":
      return store.multileaders.base;
    case "hatches":
      return store.hatches.base;
    case "blockInserts":
      return store.blockInserts.base;
    case "arrayRects":
      return store.arrayRects.base;
    case "viewports":
      return store.viewports.base;
    case "revisionClouds":
      return store.revisionClouds.base;
    case "wipeouts":
      return store.wipeouts.base;
    case "polylines":
      return store.polylines.base;
    case "tables":
      return store.tables.base;
    case "oleFrames":
      return store.oleFrames.base;
    default:
      return undefined;
  }
}

/**
 * SINGLE SOURCE OF TRUTH for the live-entity walk over a holder's compact
 * cache. Walks `idIndex` (Map → insertion order), skips tombstoned slots and
 * ATTDEF (block-children only), materialises each surviving slot via
 * `entityFromCompactStore`, and yields it. An optional `spaceId` restricts the
 * walk to one space; omit it to visit the WHOLE document in one pass.
 *
 * Every public walker (`forEachLiveEntityInSpace`, `forEachLiveEntity`,
 * `iterateLiveEntitiesInSpace`) delegates here so the iteration order /
 * tombstone filter / ATTDEF skip predicate has exactly ONE definition — the
 * three variants can never drift out of lockstep (the prior duplicated bodies
 * carried a "MUST stay in lockstep" warning; this collapses them).
 *
 * Each entity object is freshly constructed and becomes GC-eligible as soon as
 * the consumer is done with it, so a generator-driven consumer holds only one
 * materialised entity at a time (the ~150 MB-peak-avoidance contract for 685k+
 * drawings).
 *
 * PR #398 @codex P2 (T2 round-2) — the `spaceId === undefined` (holder-wide)
 * mode backs the AI snapshot's single O(N) pass across all spaces, replacing
 * the O(spaces × N) per-space read-through getter cost.
 */
function* iterateLiveEntities(
  holder: DocumentMemoryHolder,
  spaceId?: string,
): Generator<DrawingEntity, void, undefined> {
  const store = holder.compactStore;
  for (const location of holder.idIndex.values()) {
    const { storeKind, index } = location;
    if (storeKind === "attdefs") continue;
    if (storeKind === "lines") {
      if (store.lines.tombstones[index] === 1) continue;
      if (spaceId !== undefined && store.lines.spaceIds[index] !== spaceId) {
        continue;
      }
      const entity = entityFromCompactStore(store, "lines", index);
      if (entity && entity.type !== "Attdef") yield entity;
      continue;
    }
    const base = baseColumnsForKind(store, storeKind);
    if (base === undefined) continue;
    if (base.tombstones[index] === 1) continue;
    if (spaceId !== undefined && base.spaceIds[index] !== spaceId) continue;
    const entity = entityFromCompactStore(store, storeKind, index);
    if (entity && entity.type !== "Attdef") yield entity;
  }
}

/**
 * Phase 4 Wave 2 (П35Р follow-up) — streaming variant of
 * `snapshotSpaceEntitiesFromHolder`. Walks every live (non-tombstoned)
 * entity in `spaceId` and invokes `cb` per-entity. Each entity object
 * is freshly constructed by `entityFromCompactStore` and becomes
 * eligible for GC once `cb` returns — so the peak heap holds only
 * **one** materialised entity at a time, not the full per-space
 * array.
 *
 * Required for 685k+ drawings where building a full DrawingEntity[]
 * snapshot just to walk it (e.g. metadata-index rebuild) would
 * re-introduce the very ~150 MB peak the streaming-import path is
 * designed to avoid.
 *
 * Same iteration order, tombstone filter, and ATTDEF skip as
 * `snapshotSpaceEntitiesFromHolder` (shared `iterateLiveEntities` core).
 */
export function forEachLiveEntityInSpace(
  holder: DocumentMemoryHolder,
  spaceId: string,
  cb: (entity: DrawingEntity) => void,
): void {
  for (const entity of iterateLiveEntities(holder, spaceId)) cb(entity);
}

/**
 * PR #398 @codex P2 (T2 round-2) — holder-wide single-pass walker. Iterates
 * EVERY live (non-tombstoned) entity across ALL spaces in ONE linear scan of
 * `idIndex`, invoking `cb` per entity. This is the space-AGNOSTIC sibling of
 * `forEachLiveEntityInSpace` (shares the `iterateLiveEntities` core, omitting
 * the `spaceId` predicate), so it visits the whole document in a single
 * O(total cache count) pass instead of one per-space pass.
 *
 * Why it exists: the AI before/after entity snapshot
 * (`collectAiEntitySnapshot`) needs a flat id→entity map across every space.
 * Reaching it via the read-through `space.entities` getter — which calls
 * `snapshotSpaceEntities(docId, space.id)` per space, each a FULL `idIndex`
 * walk filtered by that one space — costs O(spaces × N) per snapshot, ×2 per
 * AI turn (before + after). On a large multi-layout streamed/virtualized doc
 * that freezes a normal agent command. One holder-wide walk collapses it back
 * to O(N).
 *
 * ATTDEF skip is preserved deliberately: attdefs are block-definition children
 * only (never in model/paper space), so the AI snapshot surfaces them from the
 * `document.blocks[].entities` pass — mirroring the per-space readers, which
 * also drop them. The `cb` therefore never receives an Attdef.
 */
export function forEachLiveEntity(
  holder: DocumentMemoryHolder,
  cb: (entity: DrawingEntity) => void,
): void {
  for (const entity of iterateLiveEntities(holder)) cb(entity);
}

/**
 * Codex P1 follow-up (autocheck finding 2026-05-18) — generator
 * variant of `forEachLiveEntityInSpace` so async callers can yield to
 * the event loop mid-walk every N entities. The callback-based
 * function is synchronous and blocks the main thread for the full
 * walk duration (~500ms+ on 685k single-space drawings); the
 * generator lets the consumer interleave `await yieldFrame()` between
 * batches of yielded entities, restoring the chunked-yield semantics
 * the prior `buf: DrawingEntity[]` path provided WITHOUT
 * materialising a per-walk DrawingEntity[] (the buf was ~150 MB on
 * P35R 685k — see Codex P1 / `rebuildEntityMetadataIndexFromImport
 * Async`).
 *
 * Same iteration order, tombstone filter, and ATTDEF skip as
 * `forEachLiveEntityInSpace` (shared `iterateLiveEntities` core).
 */
export function iterateLiveEntitiesInSpace(
  holder: DocumentMemoryHolder,
  spaceId: string,
): Generator<DrawingEntity, void, undefined> {
  return iterateLiveEntities(holder, spaceId);
}

/**
 * Re-export so callers that already work with a holder can convert
 * an entity payload directly without going through mutationStore. Used
 * by tests that build a holder by hand.
 *
 * Returns the entity if found and not tombstoned; `undefined` otherwise.
 */
export function entityFromHolderById(
  holder: DocumentMemoryHolder,
  entityId: string,
): DrawingEntity | AttdefEntity | undefined {
  const location = holder.idIndex.get(entityId);
  if (location === undefined) return undefined;
  return entityFromCompactStore(
    holder.compactStore,
    location.storeKind as CompactStoreKind,
    location.index,
  );
}
